'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { formatRelativeDate, formatNumber } from '@/lib/utils/format'
import {
  listContentRatings,
  getContentRatingDistribution,
  getMyContentRating,
  canReviewContent,
  submitContentRating,
  updateContentRating,
  deleteContentRating,
} from '@/lib/ratings/actions'
import type { RatingWithReviewer } from '@/lib/ratings/store'

/**
 * Unified reviews UI for any rateable content type (agent | project | skill).
 * Reads/writes go through the generic ratings server actions (content_ratings).
 * Eligibility (§8 vault-OR-use) is computed server-side via canReviewContent and
 * gates the rate form — no "always show, 403 on submit".
 */

export type ReviewContentType = 'agent' | 'project' | 'skill' | 'prompt'

interface ContentReviewsProps {
  contentType: ReviewContentType
  contentId: string
  /** Optional denormalized aggregate; derived from the distribution if omitted. */
  rating?: number
  ratingCount?: number
  isOwner: boolean
  /** Lowercase noun for copy, e.g. "agent", "project", "skill". */
  typeLabel?: string
}

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest'

function StarDisplay({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5'
  return (
    <span role="img" className="flex items-center gap-0.5" aria-label={`${rating.toFixed(1)} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={sizeClass}
          viewBox="0 0 24 24"
          fill={star <= Math.round(rating) ? 'var(--color-brand-amber)' : 'none'}
          stroke={star <= Math.round(rating) ? 'var(--color-brand-amber)' : 'var(--color-text-muted)'}
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </span>
  )
}

function RateForm({
  contentType,
  contentId,
  existing,
  typeLabel,
  onDone,
  onCancel,
}: {
  contentType: ReviewContentType
  contentId: string
  existing: RatingWithReviewer | null
  typeLabel: string
  onDone: () => void
  onCancel: () => void
}) {
  const [stars, setStars] = useState(existing?.rating ?? 0)
  const [hover, setHover] = useState(0)
  const [review, setReview] = useState(existing?.review ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (stars < 1) {
      setError('Please select a star rating.')
      return
    }
    setSubmitting(true)
    setError(null)
    const body = { rating: stars, review: review.trim() || null }
    const result = existing
      ? await updateContentRating(existing.id, body)
      : await submitContentRating(contentType, contentId, body)
    setSubmitting(false)
    if (result.success) onDone()
    else setError(result.error ?? 'Something went wrong.')
  }

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
      <h4 className="text-[var(--text-sm)] font-semibold text-[var(--color-text-primary)]">
        {existing ? 'Edit your review' : `Rate this ${typeLabel}`}
      </h4>
      <div className="mt-3 flex items-center gap-2">
        <div className="flex items-center gap-1" role="radiogroup" aria-label="Star rating">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={stars === s}
              aria-label={`${s} star${s > 1 ? 's' : ''}`}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setStars(s)}
              className="p-0.5"
            >
              <svg
                className="h-7 w-7"
                viewBox="0 0 24 24"
                fill={s <= (hover || stars) ? 'var(--color-brand-amber)' : 'none'}
                stroke={s <= (hover || stars) ? 'var(--color-brand-amber)' : 'var(--color-text-muted)'}
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          ))}
        </div>
        {stars > 0 && (
          <span className="text-[var(--text-sm)] text-[var(--color-text-muted)] tabular-nums">{stars}/5</span>
        )}
      </div>
      <textarea
        value={review}
        onChange={(e) => setReview(e.target.value)}
        maxLength={2000}
        rows={4}
        placeholder="Write a review (optional, max 2000 characters)"
        className="mt-3 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-primary)] p-3 text-[var(--text-sm)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]"
      />
      <div className="mt-1 flex items-center justify-between">
        {error ? (
          <span className="text-[var(--text-xs)] text-[var(--color-status-error)]">{error}</span>
        ) : (
          <span />
        )}
        <span className="text-[var(--text-xs)] text-[var(--color-text-muted)] tabular-nums">{review.length}/2000</span>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-[var(--radius-md)] px-4 py-2 text-[var(--text-sm)] font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-4 py-2 text-[var(--text-sm)] font-medium text-white transition-colors hover:bg-[var(--color-brand-violet)] disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit Review'}
        </button>
      </div>
    </div>
  )
}

/** A review counts as "edited" if updated noticeably after it was created. */
function isEdited(r: RatingWithReviewer): boolean {
  if (!r.updated_at || !r.created_at) return false
  return new Date(r.updated_at).getTime() - new Date(r.created_at).getTime() > 60_000
}

export function ContentReviews({
  contentType,
  contentId,
  rating,
  ratingCount,
  isOwner,
  typeLabel = contentType,
}: ContentReviewsProps) {
  const [ratings, setRatings] = useState<RatingWithReviewer[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [distribution, setDistribution] = useState<Record<number, number>>({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 })
  const [userRating, setUserRating] = useState<RatingWithReviewer | null>(null)
  const [canReview, setCanReview] = useState(false)
  const [loggedIn, setLoggedIn] = useState(false)
  const limit = 10

  const fetchRatings = useCallback(
    async (newOffset = 0) => {
      setLoading(true)
      const result = await listContentRatings(contentType, contentId, { limit, offset: newOffset })
      if (result.success && result.data) {
        let sorted = result.data.data
        switch (sortBy) {
          case 'oldest':
            sorted = [...sorted].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            break
          case 'highest':
            sorted = [...sorted].sort((a, b) => b.rating - a.rating)
            break
          case 'lowest':
            sorted = [...sorted].sort((a, b) => a.rating - b.rating)
            break
        }
        setRatings((prev) => (newOffset === 0 ? sorted : [...prev, ...sorted]))
        setTotal(result.data.total)
      }
      setLoading(false)
    },
    [contentType, contentId, sortBy],
  )

  const fetchMeta = useCallback(async () => {
    const dist = await getContentRatingDistribution(contentType, contentId)
    if (dist.success && dist.data) setDistribution(dist.data)
    if (isOwner) return
    const [mine, elig] = await Promise.all([
      getMyContentRating(contentType, contentId),
      canReviewContent(contentType, contentId),
    ])
    // A successful `mine` call means the viewer is authenticated.
    setLoggedIn(mine.success)
    if (mine.success) setUserRating(mine.data ?? null)
    if (elig.success && elig.data) setCanReview(elig.data.canReview)
  }, [contentType, contentId, isOwner])

  useEffect(() => {
    fetchRatings(0)
    fetchMeta()
  }, [fetchRatings, fetchMeta])

  const refresh = () => {
    setOffset(0)
    setShowForm(false)
    fetchRatings(0)
    fetchMeta()
  }

  const totalDistribution = Object.values(distribution).reduce((s, c) => s + c, 0)
  // Header aggregate: use the parent's denormalized value when provided, else
  // derive from the (visible-only) distribution so the component is self-contained.
  const derivedAvg =
    totalDistribution > 0
      ? [1, 2, 3, 4, 5].reduce((s, star) => s + star * (distribution[star] ?? 0), 0) / totalDistribution
      : 0
  const displayCount = ratingCount ?? totalDistribution
  const displayAvg = rating ?? derivedAvg
  // Own review comes from the dedicated `mine` fetch (always available even if
  // it's not on the current page), so no viewer id is needed.
  const ownReview = userRating
  const otherReviews = userRating ? ratings.filter((r) => r.id !== userRating.id) : ratings

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex flex-col gap-6 sm:flex-row">
        <div className="text-center sm:text-left">
          <div className="text-[var(--text-4xl)] font-bold tabular-nums text-[var(--color-text-primary)]">
            {displayCount > 0 ? displayAvg.toFixed(1) : '-'}
          </div>
          <StarDisplay rating={displayAvg} size="lg" />
          <p className="mt-1 text-[var(--text-sm)] text-[var(--color-text-muted)]">
            {formatNumber(displayCount)} review{displayCount !== 1 ? 's' : ''}
          </p>
        </div>
        {totalDistribution > 0 && (
          <div className="flex-1 space-y-1.5">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = distribution[star] ?? 0
              const pct = totalDistribution > 0 ? (count / totalDistribution) * 100 : 0
              return (
                <div key={star} className="flex items-center gap-2 text-[var(--text-xs)]">
                  <span className="w-3 tabular-nums text-[var(--color-text-muted)]">{star}</span>
                  <svg className="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="var(--color-brand-amber)" aria-hidden="true">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
                    <div className="h-full rounded-full bg-[var(--color-brand-amber)]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 text-right tabular-nums text-[var(--color-text-muted)]">
                    {count} ({Math.round(pct)}%)
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3">
        {ratings.length > 0 ? (
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as SortOption)
              setOffset(0)
            }}
            className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-primary)] px-2 py-1 text-[var(--text-xs)] text-[var(--color-text-primary)]"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="highest">Highest rating</option>
            <option value="lowest">Lowest rating</option>
          </select>
        ) : (
          <span />
        )}
        {!isOwner && !showForm && (canReview || userRating) && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-4 py-2 text-[var(--text-sm)] font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-elevated)]"
          >
            {userRating ? 'Edit your review' : 'Write a review'}
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <RateForm
          contentType={contentType}
          contentId={contentId}
          existing={userRating}
          typeLabel={typeLabel}
          onDone={refresh}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Ineligible hint */}
      {!isOwner && loggedIn && !userRating && !showForm && !canReview && (
        <p className="text-[var(--text-xs)] text-[var(--color-text-muted)]">
          Add this {typeLabel} to your vault
          {contentType === 'agent' ? ', or run it, ' : ' '}
          to leave a review.
        </p>
      )}

      {/* Review list */}
      {ownReview || otherReviews.length > 0 ? (
        <div className="space-y-4">
          {ownReview && !showForm && (
            <div className="rounded-[var(--radius-md)] border-2 border-[var(--color-brand-indigo)]/20 bg-[var(--color-bg-surface)] p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[var(--text-sm)] font-medium text-[var(--color-text-primary)]">
                    @{ownReview.reviewer?.username ?? 'You'}
                  </span>
                  <StarDisplay rating={ownReview.rating} />
                  <span className="rounded-full bg-[var(--color-brand-indigo)]/10 px-2 py-0.5 text-[var(--text-xs)] text-[var(--color-brand-indigo)]">
                    Your review
                  </span>
                  {isEdited(ownReview) && (
                    <span className="text-[var(--text-xs)] text-[var(--color-text-muted)]">(edited)</span>
                  )}
                </div>
                <span className="text-[var(--text-xs)] text-[var(--color-text-muted)]">
                  {formatRelativeDate(ownReview.created_at)}
                </span>
              </div>
              {ownReview.review && (
                <p className="mt-2 text-[var(--text-sm)] text-[var(--color-text-secondary)]">{ownReview.review}</p>
              )}
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(true)}
                  className="text-[var(--text-xs)] text-[var(--color-brand-indigo)] hover:underline"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Delete your review?')) return
                    const result = await deleteContentRating(ownReview.id)
                    if (result.success) refresh()
                  }}
                  className="text-[var(--text-xs)] text-[var(--color-status-error)] hover:underline"
                >
                  Delete
                </button>
              </div>
            </div>
          )}

          {otherReviews.map((review) => (
            <div
              key={review.id}
              className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {review.reviewer?.avatar_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={review.reviewer.avatar_url} alt="" className="h-6 w-6 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-bg-elevated)] text-[10px] font-bold text-[var(--color-text-muted)]">
                      {(review.reviewer?.username ?? '?')[0].toUpperCase()}
                    </span>
                  )}
                  <Link
                    href={`/profile/${review.reviewer?.username ?? ''}`}
                    className="text-[var(--text-sm)] font-medium text-[var(--color-text-primary)] hover:underline"
                  >
                    @{review.reviewer?.username ?? 'Anonymous'}
                  </Link>
                  <StarDisplay rating={review.rating} />
                  {isEdited(review) && (
                    <span className="text-[var(--text-xs)] text-[var(--color-text-muted)]">(edited)</span>
                  )}
                </div>
                <span className="text-[var(--text-xs)] text-[var(--color-text-muted)]">
                  {formatRelativeDate(review.created_at)}
                </span>
              </div>
              {review.review && (
                <p className="mt-2 text-[var(--text-sm)] text-[var(--color-text-secondary)]">{review.review}</p>
              )}
            </div>
          ))}

          {ratings.length < total && (
            <button
              type="button"
              onClick={() => {
                const newOffset = offset + limit
                setOffset(newOffset)
                fetchRatings(newOffset)
              }}
              disabled={loading}
              className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] py-2 text-[var(--text-sm)] font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load more reviews'}
            </button>
          )}
        </div>
      ) : (
        !loading &&
        !showForm && (
          <div className="py-6 text-center">
            <p className="text-[var(--text-sm)] text-[var(--color-text-muted)]">
              No reviews yet.
              {!isOwner && canReview ? ` Be the first to review this ${typeLabel}.` : ''}
            </p>
          </div>
        )
      )}

      {loading && ratings.length === 0 && (
        <div className="flex justify-center py-8">
          <svg className="h-6 w-6 animate-spin text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  )
}
